import { useId, useState } from 'react';
import { useI18n } from '../i18n/useI18n';
import { Button } from './primitives';

/**
 * A value that exists to be copied somewhere else, shown as such.
 *
 * There is exactly one of these in the console so far — the address a tenancy
 * has to register at its identity provider — and it is the reason the component
 * exists: that string is compared byte for byte at the other end, so it has to
 * be readable in full, selectable, and copyable without a person retyping it.
 * A truncated line in small print would be the commonest way to get a sign-in
 * that never works.
 *
 * Copying can fail — a page served over plain HTTP has no clipboard — and when
 * it does the field says so rather than pretending, because the value is still
 * there to be selected by hand.
 */
export function CopyField({ label, value }: { label: string; value: string }) {
  const { t } = useI18n();
  const id = useId();
  const [state, setState] = useState<'idle' | 'copied' | 'failed'>('idle');

  async function copy() {
    const clipboard: Clipboard | undefined = navigator.clipboard;
    if (clipboard === undefined) {
      setState('failed');
      return;
    }
    try {
      await clipboard.writeText(value);
      setState('copied');
    } catch {
      setState('failed');
    }
  }

  return (
    <div className="copy-field">
      <label htmlFor={id}>{label}</label>
      <div className="copy-row">
        <input
          id={id}
          className="input mono"
          readOnly
          value={value}
          onFocus={(event) => {
            event.target.select();
          }}
        />
        <Button size="sm" onClick={() => void copy()}>
          {t('common.copy')}
        </Button>
      </div>
      <p className="hint" role="status">
        {state === 'copied' ? t('common.copied') : state === 'failed' ? t('common.copyByHand') : ''}
      </p>
    </div>
  );
}
